import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    addDoc,
    getDocs,
    query,
    orderBy,
    writeBatch,
    doc,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    AlertCircle,
    CheckCircle,
    X,
    Loader2,
    Building2,
    Eye,
    Save,
    AlertTriangle,
    Calendar,
    Tag,
    MapPin,
    CreditCard,
    TrendingUp,
    Receipt,
    FileText,
} from "lucide-react";
import XFinanceMappingModal from "./XFinanceMappingModal";
import XFinanceImportForm from "./XFinanceImportForm";

const XFinanceImportData = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();

    // Estados principales
    const [loading, setLoading] = useState(false);
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [incomeCategories, setIncomeCategories] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [csvFile, setCsvFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [errors, setErrors] = useState([]);
    const [validRows, setValidRows] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);

    // Estados para mapeo
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [missingExpenseCategories, setMissingExpenseCategories] = useState([]);
    const [missingIncomeCategories, setMissingIncomeCategories] = useState([]);
    const [missingMethods, setMissingMethods] = useState([]);
    const [expenseCategoryMappings, setExpenseCategoryMappings] = useState({});
    const [incomeCategoryMappings, setIncomeCategoryMappings] = useState({});
    const [methodMappings, setMethodMappings] = useState({});

    // Configuración
    const currentConfig = {
        title: "Importar Gastos e Ingresos",
        description: "Importe gastos e ingresos masivamente desde un archivo CSV",
        navigateBack: "/dashboard",
        color: "purple",
        icon: FileText,
    };

    // Plantilla CSV mejorada
    const currentTemplate = [
        {
            tipo: "gasto",
            descripcion: "Supermercado Walmart",
            categoria: "Alimentación",
            cantidad: "245.50",
            metodoPago: "Visa Principal",
            metodoCobro: "",
            fecha: "2025-08-16",
            ubicacion: "Plaza Central",
            notas: "Compras semanales",
            etiquetas: "supermercado,semanal",
            fuente: "",
            cliente: "",
            proyecto: "",
            tipoIngreso: "",
            horasTrabajadas: "",
            tarifaPorHora: "",
            esRecurrente: "false",
            frecuenciaRecurrencia: "",
            fechaFin: ""
        },
        {
            tipo: "gasto",
            descripcion: "Gasolina Shell",
            categoria: "Transporte",
            cantidad: "650.00",
            metodoPago: "Efectivo",
            metodoCobro: "",
            fecha: "2025-08-15",
            ubicacion: "Gasolinera Shell",
            notas: "Tanque lleno",
            etiquetas: "combustible,auto",
            fuente: "",
            cliente: "",
            proyecto: "",
            tipoIngreso: "",
            horasTrabajadas: "",
            tarifaPorHora: "",
            esRecurrente: "false",
            frecuenciaRecurrencia: "",
            fechaFin: ""
        },
        {
            tipo: "ingreso",
            descripcion: "Salario Agosto",
            categoria: "Trabajo",
            cantidad: "15000.00",
            metodoPago: "",
            metodoCobro: "Transferencia Bancaria",
            fecha: "2025-08-31",
            ubicacion: "Oficina",
            notas: "Salario mensual",
            etiquetas: "salario,mensual",
            fuente: "Empresa XYZ",
            cliente: "",
            proyecto: "",
            tipoIngreso: "unico",
            horasTrabajadas: "",
            tarifaPorHora: "",
            esRecurrente: "true",
            frecuenciaRecurrencia: "mensual",
            fechaFin: ""
        },
        {
            tipo: "ingreso",
            descripcion: "Proyecto Web",
            categoria: "Freelance",
            cantidad: "",
            metodoPago: "",
            metodoCobro: "PayPal",
            fecha: "2025-08-20",
            ubicacion: "Remoto",
            notas: "Desarrollo completo",
            etiquetas: "freelance,web",
            fuente: "Cliente ABC",
            cliente: "Empresa ABC",
            proyecto: "Sitio Web Corporativo",
            tipoIngreso: "por_horas",
            horasTrabajadas: "20",
            tarifaPorHora: "425",
            esRecurrente: "false",
            frecuenciaRecurrencia: "",
            fechaFin: ""
        }
    ];

    // Campos requeridos
    const currentRequiredFields = ["tipo", "descripcion", "categoria", "fecha"];

    // Headers CSV
    const getHeadersForType = () => {
        return [
            "tipo", "descripcion", "categoria", "cantidad", "metodoPago", "metodoCobro",
            "fecha", "ubicacion", "notas", "etiquetas", "fuente", "cliente", "proyecto",
            "tipoIngreso", "horasTrabajadas", "tarifaPorHora", "esRecurrente",
            "frecuenciaRecurrencia", "fechaFin"
        ];
    };

    // Métodos de pago predefinidos para ingresos
    const paymentMethodsData = [
        "Transferencia Bancaria",
        "Efectivo",
        "Cheque",
        "PayPal",
        "Tarjeta de Crédito",
        "Criptomonedas",
        "Otros",
    ];

    // Colores del tema
    const colors = {
        bg: "bg-purple-50",
        border: "border-purple-200",
        text: "text-purple-900",
        textSecondary: "text-purple-700",
        textTertiary: "text-purple-800",
        button: "bg-purple-600 hover:bg-purple-700",
        iconBg: "bg-purple-100",
        iconColor: "text-purple-600"
    };

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar datos
    useEffect(() => {
        if (user) {
            loadExpenseCategories();
            loadIncomeCategories();
            loadPaymentMethods();
        }
    }, [user]);

    // Funciones de carga de datos
    const loadExpenseCategories = async () => {
        if (!user) return;
        try {
            const categoriesRef = collection(db, "expenses", user.uid, "expenseCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);
            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({ id: doc.id, ...doc.data() });
            });
            setExpenseCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías de gastos:", error);
        }
    };

    const loadIncomeCategories = async () => {
        if (!user) return;
        try {
            const categoriesRef = collection(db, "incomes", user.uid, "incomeCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);
            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({ id: doc.id, ...doc.data() });
            });
            setIncomeCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías de ingresos:", error);
        }
    };

    const loadPaymentMethods = async () => {
        if (!user) return;
        try {
            const methodsRef = collection(db, "expenses", user.uid, "paymentMethods");
            const q = query(methodsRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);
            const methodsData = [];
            querySnapshot.forEach((doc) => {
                methodsData.push({ id: doc.id, ...doc.data() });
            });
            setPaymentMethods(methodsData);
        } catch (error) {
            console.error("Error al cargar métodos de pago:", error);
        }
    };

    // Descargar plantilla
    const downloadTemplate = () => {
        const headers = getHeadersForType();
        const csvContent = [
            headers.join(","),
            ...currentTemplate.map(row =>
                headers.map(header => {
                    const value = row[header] || "";
                    return value.includes(",") || value.includes('"')
                        ? `"${value.replace(/"/g, '""')}"`
                        : value;
                }).join(",")
            )
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `plantilla_gastos_ingresos_xfinance.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setMessage({ text: "Plantilla descargada correctamente", type: "success" });
    };

    // Manejar subida de archivo
    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
            setMessage({ text: "Por favor seleccione un archivo CSV válido", type: "error" });
            return;
        }

        setCsvFile(file);
        setLoading(true);

        try {
            const text = await file.text();
            await parseCSV(text);
        } catch (error) {
            console.error("Error al leer archivo:", error);
            setMessage({ text: "Error al leer el archivo CSV", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Parsear CSV
    const parseCSV = async (csvText) => {
        const lines = csvText.split("\n").filter(line => line.trim());
        if (lines.length < 2) {
            setMessage({ text: "El archivo CSV debe tener al menos una fila de datos", type: "error" });
            return;
        }

        const headers = lines[0].split(",").map(h => h.trim().replace(/"/g, ""));
        const missingHeaders = currentRequiredFields.filter(h => !headers.includes(h));

        if (missingHeaders.length > 0) {
            setMessage({
                text: `Faltan columnas requeridas: ${missingHeaders.join(", ")}`,
                type: "error"
            });
            return;
        }

        const rows = [];
        const validationErrors = [];

        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.length === 0) continue;

            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || "";
            });

            const rowErrors = validateRow(row, i + 1);
            if (rowErrors.length > 0) {
                validationErrors.push(...rowErrors);
            }

            rows.push({ ...row, rowNumber: i + 1 });
        }

        const missing = findMissingItems(rows);

        setParsedData(rows);
        setErrors(validationErrors);
        setMissingExpenseCategories(missing.expenseCategories);
        setMissingIncomeCategories(missing.incomeCategories);
        setMissingMethods(missing.methods);

        if (missing.expenseCategories.length > 0 || missing.incomeCategories.length > 0 || missing.methods.length > 0) {
            setShowMappingModal(true);
            setMessage({
                text: `Se encontraron categorías/métodos no existentes. Configure los mapeos.`,
                type: "error"
            });
        } else {
            processValidRows(rows, validationErrors);
        }

        setShowPreview(true);
    };

    // Parsear línea CSV
    const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += char;
            }
        }
        result.push(current);
        return result;
    };

    // Validar fila
    const validateRow = (row, rowNumber) => {
        const errors = [];

        if (!row.tipo?.trim()) {
            errors.push({ row: rowNumber, field: "tipo", message: "El tipo es requerido (gasto o ingreso)" });
        } else if (!["gasto", "ingreso", "expense", "income"].includes(row.tipo.toLowerCase())) {
            errors.push({ row: rowNumber, field: "tipo", message: "El tipo debe ser 'gasto' o 'ingreso'" });
        }

        const rowType = row.tipo?.toLowerCase();
        const isRowExpense = rowType === 'gasto' || rowType === 'expense';
        const isRowIncome = rowType === 'ingreso' || rowType === 'income';

        if (!row.descripcion?.trim()) {
            errors.push({ row: rowNumber, field: "descripcion", message: "La descripción es requerida" });
        }

        if (!row.categoria?.trim()) {
            errors.push({ row: rowNumber, field: "categoria", message: "La categoría es requerida" });
        }

        const cantidad = parseFloat(row.cantidad);
        if (isRowExpense) {
            if (!row.cantidad?.trim() || isNaN(cantidad) || cantidad <= 0) {
                errors.push({ row: rowNumber, field: "cantidad", message: "La cantidad es requerida y debe ser mayor a 0 para gastos" });
            }
        } else if (isRowIncome) {
            if (row.tipoIngreso === "por_horas") {
                if (!row.horasTrabajadas || isNaN(parseFloat(row.horasTrabajadas))) {
                    errors.push({ row: rowNumber, field: "horasTrabajadas", message: "Las horas trabajadas son requeridas para tipo 'por_horas'" });
                }
                if (!row.tarifaPorHora || isNaN(parseFloat(row.tarifaPorHora))) {
                    errors.push({ row: rowNumber, field: "tarifaPorHora", message: "La tarifa por hora es requerida para tipo 'por_horas'" });
                }
            } else {
                if (!row.cantidad?.trim() || isNaN(cantidad) || cantidad <= 0) {
                    errors.push({ row: rowNumber, field: "cantidad", message: "La cantidad es requerida y debe ser mayor a 0 para ingresos" });
                }
            }
        }

        if (isRowExpense) {
            if (!row.metodoPago?.trim()) {
                errors.push({ row: rowNumber, field: "metodoPago", message: "El método de pago es requerido para gastos" });
            }
        } else if (isRowIncome) {
            if (!row.metodoCobro?.trim()) {
                errors.push({ row: rowNumber, field: "metodoCobro", message: "El método de cobro es requerido para ingresos" });
            }
        }

        if (!row.fecha?.trim()) {
            errors.push({ row: rowNumber, field: "fecha", message: "La fecha es requerida" });
        } else {
            const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
            if (!dateRegex.test(row.fecha)) {
                errors.push({ row: rowNumber, field: "fecha", message: "La fecha debe estar en formato YYYY-MM-DD" });
            } else {
                const date = new Date(row.fecha);
                if (isNaN(date.getTime())) {
                    errors.push({ row: rowNumber, field: "fecha", message: "La fecha no es válida" });
                }
            }
        }

        if (isRowIncome) {
            if (row.tipoIngreso && !["unico", "recurrente", "por_horas"].includes(row.tipoIngreso)) {
                errors.push({ row: rowNumber, field: "tipoIngreso", message: "El tipo de ingreso debe ser: unico, recurrente o por_horas" });
            }
        }

        // Validar campos de recurrencia
        if (row.esRecurrente && (row.esRecurrente.toLowerCase() === "true" || row.esRecurrente === "1")) {
            if (!row.frecuenciaRecurrencia?.trim()) {
                errors.push({ row: rowNumber, field: "frecuenciaRecurrencia", message: "La frecuencia de recurrencia es requerida cuando es recurrente" });
            }
        }

        return errors;
    };

    // Encontrar elementos faltantes
    const findMissingItems = (rows) => {
        const missingExpenseCats = new Set();
        const missingIncomeCats = new Set();
        const missingMets = new Set();

        rows.forEach(row => {
            const rowType = row.tipo?.toLowerCase();
            const isRowExpense = rowType === 'gasto' || rowType === 'expense';
            const isRowIncome = rowType === 'ingreso' || rowType === 'income';

            if (row.categoria?.trim()) {
                if (isRowExpense) {
                    const categoryExists = expenseCategories.some(cat =>
                        cat.nombre.toLowerCase() === row.categoria.toLowerCase()
                    );
                    if (!categoryExists) {
                        missingExpenseCats.add(row.categoria);
                    }
                } else if (isRowIncome) {
                    const categoryExists = incomeCategories.some(cat =>
                        cat.nombre.toLowerCase() === row.categoria.toLowerCase()
                    );
                    if (!categoryExists) {
                        missingIncomeCats.add(row.categoria);
                    }
                }
            }

            if (isRowExpense && row.metodoPago?.trim()) {
                const methodExists = paymentMethods.some(method =>
                    method.nombre.toLowerCase() === row.metodoPago.toLowerCase()
                );
                if (!methodExists) {
                    missingMets.add(row.metodoPago);
                }
            } else if (isRowIncome && row.metodoCobro?.trim()) {
                const methodExists = paymentMethodsData.some(method =>
                    method.toLowerCase() === row.metodoCobro.toLowerCase()
                );
                if (!methodExists) {
                    missingMets.add(row.metodoCobro);
                }
            }
        });

        return {
            expenseCategories: Array.from(missingExpenseCats),
            incomeCategories: Array.from(missingIncomeCats),
            methods: Array.from(missingMets)
        };
    };

    // Procesar filas válidas - MEJORADO
    const processValidRows = (rows, validationErrors) => {
        const validRows = [];

        rows.forEach(row => {
            const hasErrors = validationErrors.some(error => error.row === row.rowNumber);
            if (!hasErrors) {
                const rowType = row.tipo?.toLowerCase();

                let finalCategory;
                if (rowType === 'gasto' || rowType === 'expense') {
                    finalCategory = expenseCategoryMappings[row.categoria] || row.categoria;
                } else {
                    finalCategory = incomeCategoryMappings[row.categoria] || row.categoria;
                }

                const methodField = (rowType === 'gasto' || rowType === 'expense') ? 'metodoPago' : 'metodoCobro';
                const finalMethod = methodMappings[row[methodField]] || row[methodField];

                let categoryExists = false;
                if (rowType === 'gasto' || rowType === 'expense') {
                    categoryExists = expenseCategories.some(cat =>
                        cat.nombre.toLowerCase() === finalCategory.toLowerCase()
                    );
                } else {
                    categoryExists = incomeCategories.some(cat =>
                        cat.nombre.toLowerCase() === finalCategory.toLowerCase()
                    );
                }

                let methodExists = true;
                if (rowType === 'gasto' || rowType === 'expense') {
                    methodExists = paymentMethods.some(method =>
                        method.nombre.toLowerCase() === finalMethod.toLowerCase()
                    );
                } else {
                    methodExists = paymentMethodsData.some(method =>
                        method.toLowerCase() === finalMethod.toLowerCase()
                    );
                }

                if (categoryExists && methodExists) {
                    // Estructura base común
                    let processedRow = {
                        descripcion: row.descripcion.trim(),
                        categoria: finalCategory,
                        cantidad: parseFloat(row.cantidad) || 0,
                        fecha: row.fecha.trim(),
                        ubicacion: row.ubicacion?.trim() || "",
                        notas: row.notas?.trim() || "",
                        etiquetas: row.etiquetas ? row.etiquetas.split(",").map(t => t.trim()).filter(t => t) : [],

                        // Campos de recurrencia
                        esRecurrente: row.esRecurrente ?
                            (row.esRecurrente.toLowerCase() === "true" || row.esRecurrente === "1") : false,
                        frecuenciaRecurrencia: row.frecuenciaRecurrencia?.trim() || "mensual",
                        fechaFin: row.fechaFin?.trim() || "",

                        // Campos de auditoría
                        userId: user.uid,
                        createdAt: new Date(),
                        updatedAt: new Date(),

                        // Campo de identificación para el proceso
                        _importType: (rowType === 'gasto' || rowType === 'expense') ? 'gasto' : 'ingreso'
                    };

                    if (rowType === 'gasto' || rowType === 'expense') {
                        // Campos específicos de gastos
                        processedRow.metodoPago = finalMethod;
                        processedRow.comprobanteUrl = "";

                        if (processedRow.cantidad <= 0) return;
                    } else {
                        // Campos específicos de ingresos
                        processedRow.metodoCobro = finalMethod;
                        processedRow.fuente = row.fuente?.trim() || "";
                        processedRow.cliente = row.cliente?.trim() || "";
                        processedRow.proyecto = row.proyecto?.trim() || "";
                        processedRow.tipoIngreso = row.tipoIngreso?.trim() || "unico";
                        processedRow.comprobanteUrl = "";

                        // Manejo especial para ingresos por horas
                        if (row.horasTrabajadas && row.tarifaPorHora) {
                            processedRow.horasTrabajadas = parseFloat(row.horasTrabajadas) || null;
                            processedRow.tarifaPorHora = parseFloat(row.tarifaPorHora) || null;

                            if (processedRow.tipoIngreso === "por_horas" && processedRow.horasTrabajadas && processedRow.tarifaPorHora) {
                                processedRow.cantidad = processedRow.horasTrabajadas * processedRow.tarifaPorHora;
                            }
                        } else {
                            processedRow.horasTrabajadas = null;
                            processedRow.tarifaPorHora = null;
                        }

                        if (processedRow.cantidad <= 0 && processedRow.tipoIngreso !== "por_horas") return;
                    }

                    validRows.push(processedRow);
                }
            }
        });

        setValidRows(validRows);

        if (validationErrors.length > 0 || missingExpenseCategories.length > 0 || missingIncomeCategories.length > 0 || missingMethods.length > 0) {
            setMessage({
                text: `Se encontraron errores de validación o elementos faltantes`,
                type: "error"
            });
        } else {
            const expenseCount = validRows.filter(row => row._importType === 'gasto').length;
            const incomeCount = validRows.filter(row => row._importType === 'ingreso').length;

            setMessage({
                text: `${validRows.length} filas listas para importar (${expenseCount} gastos, ${incomeCount} ingresos)`,
                type: "success"
            });
        }
    };

    // Completar mapeo
    const handleMappingComplete = () => {
        const incompleteExpenseCategoryMappings = missingExpenseCategories.filter(cat => !expenseCategoryMappings[cat]);
        const incompleteIncomeCategoryMappings = missingIncomeCategories.filter(cat => !incomeCategoryMappings[cat]);
        const incompleteMethodMappings = missingMethods.filter(method => !methodMappings[method]);

        if (incompleteExpenseCategoryMappings.length > 0 || incompleteIncomeCategoryMappings.length > 0 || incompleteMethodMappings.length > 0) {
            setMessage({
                text: "Por favor complete todos los mapeos antes de continuar",
                type: "error"
            });
            return;
        }

        processValidRows(parsedData, errors);
        setShowMappingModal(false);
        setMessage({
            text: "Mapeos aplicados correctamente",
            type: "success"
        });
    };

    // Importar datos - MEJORADO
    const importData = async () => {
        if (validRows.length === 0) {
            setMessage({ text: "No hay filas válidas para importar", type: "error" });
            return;
        }

        setImporting(true);
        setImportProgress(0);

        try {
            const expenseRows = validRows.filter(row => row._importType === 'gasto');
            const incomeRows = validRows.filter(row => row._importType === 'ingreso');

            let importedCount = 0;
            const totalRows = validRows.length;

            // Importar gastos
            if (expenseRows.length > 0) {
                const expensesRef = collection(db, "expenses", user.uid, "userExpenses");
                const batchSize = 250;

                for (let i = 0; i < expenseRows.length; i += batchSize) {
                    const currentBatch = writeBatch(db);
                    const batchRows = expenseRows.slice(i, i + batchSize);

                    for (const row of batchRows) {
                        const cleanRow = { ...row };
                        delete cleanRow._importType; // Remover campo temporal
                        const docRef = doc(expensesRef);
                        currentBatch.set(docRef, cleanRow);
                    }

                    await currentBatch.commit();
                    importedCount += batchRows.length;
                    setImportProgress((importedCount / totalRows) * 100);
                }
            }

            // Importar ingresos
            if (incomeRows.length > 0) {
                const incomesRef = collection(db, "incomes", user.uid, "userIncomes");
                const batchSize = 250;

                for (let i = 0; i < incomeRows.length; i += batchSize) {
                    const currentBatch = writeBatch(db);
                    const batchRows = incomeRows.slice(i, i + batchSize);

                    for (const row of batchRows) {
                        const cleanRow = { ...row };
                        delete cleanRow._importType; // Remover campo temporal
                        const docRef = doc(incomesRef);
                        currentBatch.set(docRef, cleanRow);
                    }

                    await currentBatch.commit();
                    importedCount += batchRows.length;
                    setImportProgress((importedCount / totalRows) * 100);
                }
            }

            setMessage({
                text: `${validRows.length} registros importados correctamente (${expenseRows.length} gastos, ${incomeRows.length} ingresos)`,
                type: "success"
            });

            setTimeout(() => {
                clearData();
                // Redirigir al dashboard para ver los datos importados
                navigate("/dashboard");
            }, 3000);

        } catch (error) {
            console.error(`Error al importar datos:`, error);
            setMessage({ text: `Error al importar los datos: ${error.message}`, type: "error" });
        } finally {
            setImporting(false);
        }
    };

    // Limpiar datos
    const clearData = () => {
        setCsvFile(null);
        setParsedData([]);
        setValidRows([]);
        setErrors([]);
        setShowPreview(false);
        setImportProgress(0);
        setShowMappingModal(false);
        setMissingExpenseCategories([]);
        setMissingIncomeCategories([]);
        setMissingMethods([]);
        setExpenseCategoryMappings({});
        setIncomeCategoryMappings({});
        setMethodMappings({});

        const fileInput = document.getElementById('csvFile');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(currentConfig.navigateBack)}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">{currentConfig.title}</h1>
                                <p className="text-slate-600 font-medium">{currentConfig.description}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Formulario de Importación */}
                <XFinanceImportForm
                    colors={colors}
                    csvFile={csvFile}
                    loading={loading}
                    handleFileUpload={handleFileUpload}
                    downloadTemplate={downloadTemplate}
                    clearData={clearData}
                    currentTemplate={currentTemplate}
                    getHeadersForType={getHeadersForType}
                />

                {/* Preview de Datos */}
                {showPreview && (
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <Eye className="w-5 h-5 text-purple-600" />
                                    </div>
                                    Paso 3: Revisar Datos
                                </h2>
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-slate-600">
                                        {validRows.length} filas válidas • {errors.length} errores
                                    </span>
                                    {errors.length === 0 && validRows.length > 0 && (
                                        <button
                                            onClick={importData}
                                            disabled={importing}
                                            className={`${colors.button} text-white px-6 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 disabled:opacity-50`}
                                        >
                                            {importing ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Importando... {Math.round(importProgress)}%
                                                </>
                                            ) : (
                                                <>
                                                    <Save className="w-4 h-4" />
                                                    Importar {validRows.length} Registros
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Estadísticas de preview */}
                            {validRows.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <TrendingUp className="w-8 h-8 text-green-600" />
                                            <div>
                                                <p className="text-sm text-green-600 font-medium">Ingresos</p>
                                                <p className="text-xl font-bold text-green-900">
                                                    {validRows.filter(row => row._importType === 'ingreso').length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <Receipt className="w-8 h-8 text-red-600" />
                                            <div>
                                                <p className="text-sm text-red-600 font-medium">Gastos</p>
                                                <p className="text-xl font-bold text-red-900">
                                                    {validRows.filter(row => row._importType === 'gasto').length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-8 h-8 text-blue-600" />
                                            <div>
                                                <p className="text-sm text-blue-600 font-medium">Recurrentes</p>
                                                <p className="text-xl font-bold text-blue-900">
                                                    {validRows.filter(row => row.esRecurrente).length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Errores */}
                            {errors.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
                                        <AlertTriangle className="w-5 h-5" />
                                        Errores de Validación ({errors.length})
                                    </h3>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                                        <div className="space-y-2">
                                            {errors.map((error, index) => (
                                                <div key={index} className="text-sm text-red-800">
                                                    <span className="font-medium">Fila {error.row}:</span> {error.message}
                                                    <span className="text-red-600"> (campo: {error.field})</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Preview de datos válidos */}
                            {validRows.length > 0 && (
                                <div>
                                    <h3 className={`text-lg font-semibold ${colors.text} mb-3 flex items-center gap-2`}>
                                        <CheckCircle className="w-5 h-5" />
                                        Datos Válidos ({validRows.length})
                                    </h3>
                                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto max-h-96">
                                            <table className="w-full text-sm">
                                                <thead className="bg-slate-50 border-b border-slate-200">
                                                    <tr>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Tipo</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Descripción</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Categoría</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Monto</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Método</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Fecha</th>
                                                        <th className="text-left p-3 font-semibold text-slate-900">Recurrente</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {validRows.slice(0, 10).map((row, index) => (
                                                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                                                            <td className="p-3">
                                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${row._importType === 'gasto'
                                                                    ? 'bg-red-100 text-red-800'
                                                                    : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                    {row._importType === 'gasto' ? <Receipt className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />}
                                                                    {row._importType === 'gasto' ? 'Gasto' : 'Ingreso'}
                                                                </span>
                                                            </td>
                                                            <td className="p-3 text-slate-900 max-w-32 truncate" title={row.descripcion}>
                                                                {row.descripcion}
                                                            </td>
                                                            <td className="p-3">
                                                                <span className={`inline-flex items-center gap-1 px-2 py-1 ${colors.iconBg} ${colors.iconColor} rounded-full text-xs font-medium`}>
                                                                    <Tag className="w-3 h-3" />
                                                                    {row.categoria}
                                                                </span>
                                                            </td>
                                                            <td className={`p-3 font-semibold ${row._importType === 'gasto' ? 'text-red-600' : 'text-green-600'}`}>
                                                                ${row.cantidad.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                            </td>
                                                            <td className="p-3">
                                                                <span className="inline-flex items-center gap-1 text-slate-600 text-xs">
                                                                    <CreditCard className="w-3 h-3" />
                                                                    {row._importType === 'gasto' ? row.metodoPago : row.metodoCobro}
                                                                </span>
                                                            </td>
                                                            <td className="p-3">
                                                                <span className="inline-flex items-center gap-1 text-slate-600 text-xs">
                                                                    <Calendar className="w-3 h-3" />
                                                                    {new Date(row.fecha).toLocaleDateString("es-MX")}
                                                                </span>
                                                            </td>
                                                            <td className="p-3">
                                                                {row.esRecurrente ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                                                        <Calendar className="w-3 h-3" />
                                                                        {row.frecuenciaRecurrencia}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-slate-400 text-xs">No</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {validRows.length > 10 && (
                                            <div className="p-3 bg-slate-50 border-t border-slate-200 text-center text-slate-600">
                                                Mostrando 10 de {validRows.length} filas
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Barra de progreso */}
                            {importing && (
                                <div className="mt-6">
                                    <div className="bg-slate-200 rounded-full h-3">
                                        <div
                                            className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                                            style={{ width: `${importProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="text-center text-slate-600 mt-2 font-medium">
                                        Importando {Math.round(importProgress)}% - {Math.round((importProgress / 100) * validRows.length)} de {validRows.length} registros
                                    </p>
                                    {importProgress > 0 && (
                                        <p className="text-center text-slate-500 text-sm mt-1">
                                            Los datos se están guardando en la base de datos...
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Información de ayuda */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-8">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Información sobre la Importación
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800 text-sm">
                        <div className="space-y-2">
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Descargue la plantilla CSV para ver el formato correcto de los datos</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Las fechas deben estar en formato YYYY-MM-DD (ej: 2025-08-16)</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Para gastos recurrentes use "true" en la columna esRecurrente</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Los ingresos por horas calculan automáticamente el monto total</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Las categorías y métodos de pago deben existir previamente</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                                <span>Separe las etiquetas con comas (ej: trabajo,freelance,web)</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Botones de gestión */}
                <div className="mt-8 flex gap-3 justify-center flex-wrap">
                    <button
                        onClick={() => navigate("/expense-categories")}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors text-sm"
                    >
                        Gestionar Categorías de Gastos
                    </button>
                    <button
                        onClick={() => navigate("/payment-methods")}
                        className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors text-sm"
                    >
                        Gestionar Métodos de Pago
                    </button>
                    <button
                        onClick={() => navigate("/income-categories")}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors text-sm"
                    >
                        Gestionar Categorías de Ingresos
                    </button>
                </div>
            </div>

            {/* Modal de Mapeo */}
            <XFinanceMappingModal
                showMappingModal={showMappingModal}
                setShowMappingModal={setShowMappingModal}
                missingExpenseCategories={missingExpenseCategories}
                missingIncomeCategories={missingIncomeCategories}
                missingMethods={missingMethods}
                expenseCategories={expenseCategories}
                incomeCategories={incomeCategories}
                paymentMethods={paymentMethods}
                paymentMethodsData={paymentMethodsData}
                expenseCategoryMappings={expenseCategoryMappings}
                setExpenseCategoryMappings={setExpenseCategoryMappings}
                incomeCategoryMappings={incomeCategoryMappings}
                setIncomeCategoryMappings={setIncomeCategoryMappings}
                methodMappings={methodMappings}
                setMethodMappings={setMethodMappings}
                parsedData={parsedData}
                handleMappingComplete={handleMappingComplete}
                colors={colors}
            />

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                        ? "bg-red-50 border border-red-200 text-red-800"
                        : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceImportData;